import { isAxiosNetworkOrTimeoutError, isAxiosRequestCanceled } from '@/utils/isAxiosRequestCanceled';
import { isBenignApexChartsNavigationNoise } from '@/utils/apexChartsBenign';
import { isDynamicImportChunkError } from '@/utils/chunkLoadError';

/**
 * Axios 401 = sessão inválida/expirada ou sem cookie Sanctum.
 * Não é bug de aplicação; o interceptor já ignora, mas o Vue errorHandler
 * ainda captura se a promise for rejeitada sem catch na UI.
 * @param {import('@sentry/core').EventHint | undefined} hint
 */
export function isExpectedAxiosUnauthorized(hint) {
    const ex = hint?.originalException;
    if (!ex || ex.name !== 'AxiosError') {
        return false;
    }
    return ex.response?.status === 401;
}

/** @deprecated use isExpectedAxiosUnauthorized */
export function isExpectedCheckAuthUnauthorized(hint) {
    return isExpectedAxiosUnauthorized(hint);
}

/**
 * 409 em rotas guest (login/register) = usuário já tem sessão Sanctum ativa.
 * Resposta intencional do backend; não é bug de aplicação.
 * @param {import('@sentry/core').EventHint | undefined} hint
 */
export function isExpectedAxiosAlreadyAuthenticated(hint) {
    const ex = hint?.originalException;
    if (!ex || ex.name !== 'AxiosError' || ex.response?.status !== 409) {
        return false;
    }

    const message = ex.response?.data?.message;
    if (typeof message === 'string' && /já está autenticado/i.test(message)) {
        return true;
    }

    const url = ex.config?.url || ex.response?.config?.url || '';
    return /\/(login|register)(\?|$)/i.test(String(url));
}

/**
 * Ruído benigno capturado pelo handler global `onerror` (ex.: Plyr dispara
 * `CustomEvent` em falhas de media element — não é uma exceção JS com stack).
 */

/**
 * @param {unknown} value
 * @returns {value is CustomEvent<any>}
 */
function isCustomEvent(value) {
    return typeof CustomEvent !== 'undefined' && value instanceof CustomEvent;
}

/**
 * Eventos de erro do Plyr chegam como CustomEvent com `detail.plyr`.
 * @param {import('@sentry/core').ErrorEvent} _event
 * @param {import('@sentry/core').EventHint | undefined} hint
 */
export function isPlyrMediaCustomErrorNoise(_event, hint) {
    const ex = hint?.originalException;
    if (isCustomEvent(ex) && ex.type === 'error') {
        const d = ex.detail;
        if (d != null && typeof d === 'object' && Object.prototype.hasOwnProperty.call(d, 'plyr')) {
            return true;
        }
    }

    const fromEvent = _event?.exception?.values?.map((v) => v?.value).filter(Boolean).join(' ') || '';
    return /CustomEvent.*type=error/i.test(fromEvent);
}

/**
 * @type {import('@sentry/vue').BrowserOptions['beforeSend']}
 */
export function sentryBeforeSend(event, hint) {
    if (isPlyrMediaCustomErrorNoise(event, hint)) {
        return null;
    }
    if (isBenignApexChartsNavigationNoise(event, hint)) {
        return null;
    }
    if (isAxiosRequestCanceled(hint?.originalException)) {
        return null;
    }
    if (isAxiosNetworkOrTimeoutError(hint?.originalException)) {
        return null;
    }
    if (isExpectedAxiosUnauthorized(hint)) {
        return null;
    }
    if (isExpectedAxiosAlreadyAuthenticated(hint)) {
        return null;
    }
    if (isDynamicImportChunkError(hint?.originalException)) {
        return null;
    }

    const fromEvent = event?.exception?.values?.map((v) => v?.value).filter(Boolean).join(' ') || '';
    if (isDynamicImportChunkError({ message: fromEvent })) {
        return null;
    }

    return event;
}
